import { CircleOff } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { PageHeader, PageShell } from '~/components/layout';
import GeneralSection from '~/components/pages/ai/settings/GeneralSection';
import ModelsSection from '~/components/pages/ai/settings/ModelsSection';
import PromptConfigSection from '~/components/pages/ai/settings/PromptConfigSection';
import { Button, Card, CardBody, EmptyState } from '~/components/ui';
import { routes } from '~/app/routes';

type SettingsSectionId = 'general' | 'ai-write' | 'models' | 'other';

const SECTION_LABELS: Record<SettingsSectionId, string> = {
  general: 'General',
  'ai-write': 'AI Write',
  models: 'Models',
  other: 'Other',
};

function isSettingsSectionId(value: string | undefined): value is SettingsSectionId {
  return value !== undefined && Object.hasOwn(SECTION_LABELS, value);
}

/**
 * The four AI settings sections share one route (`/ai/settings/:section`) and
 * the sidebar submenu chooses between them. An unknown segment renders the
 * refusal in place rather than redirecting.
 */
export default function SettingsPage() {
  const { section } = useParams<{ section: string }>();

  if (!isSettingsSectionId(section)) {
    return (
      <PageShell>
        <PageHeader title="Settings" breadcrumbs={[{ label: 'AI' }, { label: 'Settings' }]} />
        <Card>
          <CardBody>
            <EmptyState
              icon={<CircleOff className="size-8" aria-hidden="true" />}
              title="This settings section does not exist"
              description="The AI settings section you followed was not found. Choose one from the AI menu."
              action={
                <Button variant="outline" href={routes.aiSettings('general')}>
                  Go to General
                </Button>
              }
            />
          </CardBody>
        </Card>
      </PageShell>
    );
  }

  const label = SECTION_LABELS[section];

  return (
    <PageShell>
      <PageHeader title={label} breadcrumbs={[{ label: 'AI' }, { label: 'Settings' }, { label }]} />

      {section === 'general' ? <GeneralSection /> : null}
      {section === 'ai-write' ? <PromptConfigSection group="aiWrite" /> : null}
      {section === 'models' ? <ModelsSection /> : null}
      {section === 'other' ? <PromptConfigSection group="other" /> : null}
    </PageShell>
  );
}
